import { analyticsPath, scrubText, scrubUrl, type ClientTelemetryConfig } from '@fitting-room/contracts/telemetry'

/**
 * Google Analytics 4 and Sentry for the client — off unless the API wrote
 * their settings into the page (`window.__FR_CONFIG__`, from
 * `FITTING_ROOM_GA_MEASUREMENT_ID` / `FITTING_ROOM_SENTRY_DSN`).
 *
 * Nothing leaves with a credential in it. `/s/<token>` is a customer's key to
 * her fitting room, so GA is never allowed to read the address bar: its own
 * page views are off, and every page view and every `set` carries a path
 * scrubbed by `@fitting-room/contracts/telemetry`. The staff console isn't
 * counted at all. Sentry gets the same treatment in `beforeSend`.
 */

declare global {
  interface Window {
    __FR_CONFIG__?: ClientTelemetryConfig
    dataLayer?: unknown[]
    gtag?: (...args: unknown[]) => void
  }
}

const settings: ClientTelemetryConfig = (typeof window !== 'undefined' && window.__FR_CONFIG__) || {}
let gaLoaded = false

/** Sentry, once, at boot — only when a DSN was written into the page; the SDK isn't even downloaded otherwise. */
export function initTelemetry(): void {
  const dsn = settings.sentryDsn
  if (!dsn) return
  void import('@sentry/browser').then((Sentry) => {
    Sentry.init({
      dsn,
      environment: settings.environment ?? 'production',
      sendDefaultPii: false,
      tracesSampleRate: 0,
      beforeSend(event) {
        if (event.request) event.request = event.request.url ? { url: scrubUrl(event.request.url) } : {}
        delete event.user
        if (event.transaction) event.transaction = scrubText(event.transaction)
        if (event.message) event.message = scrubText(event.message)
        for (const value of event.exception?.values ?? []) {
          if (value.value) value.value = scrubText(value.value)
        }
        return event
      },
      beforeBreadcrumb(crumb) {
        // Typed input, logged to the console by a form or a library, stays here.
        if (crumb.category === 'console' || crumb.category === 'ui.input') return null
        if (crumb.message) crumb.message = scrubText(crumb.message)
        if (crumb.data) {
          const data: Record<string, unknown> = {}
          for (const [key, value] of Object.entries(crumb.data)) data[key] = typeof value === 'string' ? scrubText(value) : value
          crumb.data = data
        }
        return crumb
      },
    })
  })
}

/** The address GA may see: this origin and a scrubbed path, or nothing for a page it mustn't count. */
function pageLocation(pathname: string): { location: string; path: string } | null {
  const path = analyticsPath(pathname)
  return path ? { location: `${window.location.origin}${path}`, path } : null
}

function loadGa(id: string, first: { location: string; path: string }): void {
  window.dataLayer = window.dataLayer ?? []
  window.gtag = function gtag() {
    // gtag.js reads `arguments` itself; it has to be pushed as-is.
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer?.push(arguments)
  }
  window.gtag('js', new Date())
  window.gtag('config', id, {
    // Its own page views read the address bar, token and all: ours are sent by hand.
    send_page_view: false,
    page_location: first.location,
    page_path: first.path,
    page_referrer: document.referrer ? scrubUrl(document.referrer) : '',
  })
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`
  document.head.appendChild(script)
  gaLoaded = true
}

/**
 * A page view for the route the app is on. GA is loaded on the first page a
 * customer opens, so a staff-only visit never downloads it.
 */
export function trackPage(pathname: string): void {
  const id = settings.gaMeasurementId
  const page = id ? pageLocation(pathname) : null
  if (!id || !page) return
  if (!gaLoaded) loadGa(id, page)
  window.gtag?.('set', { page_location: page.location, page_path: page.path })
  window.gtag?.('event', 'page_view', { page_location: page.location, page_path: page.path, page_title: page.path })
}

/** A step in the booking funnel. Values are kinds and counts, never who: no names, emails, phones or references. */
export function track(event: string, params: Readonly<Record<string, string | number | boolean>> = {}): void {
  if (!gaLoaded) return
  window.gtag?.('event', event, params)
}
